/**
 * TeacherMicButton — voice input for the chat composer. Records via
 * MediaRecorder, sends the clip to Nova OS's OpenAI-compatible
 * POST /v1/audio/transcriptions (gateway whisper — Systran/
 * faster-whisper-large-v3 by default, override via settings asr_model),
 * and appends the transcript to the composer text for the teacher to
 * review/edit before sending. Click to start, click again to stop.
 *
 * Hidden entirely when the browser lacks getUserMedia/MediaRecorder
 * (or the page isn't a secure context) — no dead buttons.
 */
import { useEffect, useRef, useState } from "react";
import { useComposerRuntime } from "@assistant-ui/react";
import { useTranslation } from "react-i18next";

type MicState = "idle" | "recording" | "busy";

function micSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined"
  );
}

export function TeacherMicButton({
  authHeader,
}: {
  authHeader: Record<string, string>;
}): JSX.Element | null {
  const { t } = useTranslation();
  const composer = useComposerRuntime();
  const [state, setState] = useState<MicState>("idle");
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Stop the recorder + release the mic if the surface unmounts mid-recording.
  useEffect(
    () => () => {
      const rec = recRef.current;
      if (rec && rec.state !== "inactive") rec.stop();
      rec?.stream.getTracks().forEach((t2) => t2.stop());
    },
    [],
  );

  if (!micSupported()) return null;

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Safari records audio/mp4 (AAC); Chrome/Edge audio/webm (opus). The
      // gateway whisper accepts both — just name the part accordingly.
      const mime = MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        stream.getTracks().forEach((t2) => t2.stop());
        void transcribe(new Blob(chunksRef.current, { type: mime }), mime);
      };
      recRef.current = rec;
      rec.start();
      setState("recording");
    } catch (e) {
      setError(String((e as Error)?.message || e));
      setState("idle");
    }
  }

  function stopRecording() {
    recRef.current?.stop();
    setState("busy");
  }

  async function transcribe(blob: Blob, mime: string) {
    try {
      if (blob.size < 1000) {
        // Sub-second tap — nothing worth sending.
        setState("idle");
        return;
      }
      const form = new FormData();
      form.append(
        "file",
        blob,
        mime === "audio/mp4" ? "audio.mp4" : "audio.webm",
      );
      // No language param — whisper auto-detects, teachers mix zh/en.
      const r = await fetch("/v1/audio/transcriptions", {
        method: "POST",
        headers: authHeader,
        body: form,
      });
      if (!r.ok) throw new Error(`transcribe ${r.status}`);
      const j = (await r.json()) as { text?: string };
      const text = (j.text || "").trim();
      if (text) {
        const existing = composer.getState().text;
        composer.setText(existing ? `${existing} ${text}` : text);
      }
      setState("idle");
    } catch (e) {
      setError(String((e as Error)?.message || e));
      setState("idle");
    }
  }

  const label =
    state === "recording"
      ? t("assistantUi.micStop")
      : state === "busy"
        ? t("assistantUi.micBusy")
        : t("assistantUi.micStart");

  return (
    <button
      type="button"
      className={`teacher-mic${state === "recording" ? " teacher-mic--recording" : ""}${state === "busy" ? " teacher-mic--busy" : ""}`}
      aria-label={String(label)}
      title={error ? `${label} — ${error}` : String(label)}
      disabled={state === "busy"}
      onClick={() => {
        if (state === "recording") stopRecording();
        else if (state === "idle") void startRecording();
      }}
    >
      {state === "recording" ? (
        // Stop square while recording.
        <svg viewBox="0 0 16 16" fill="currentColor">
          <rect x="4" y="4" width="8" height="8" rx="1" />
        </svg>
      ) : (
        // Microphone.
        <svg
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        >
          <rect x="6" y="1.5" width="4" height="8" rx="2" />
          <path d="M3.5 7.5a4.5 4.5 0 0 0 9 0" />
          <path d="M8 12v2.5M5.5 14.5h5" />
        </svg>
      )}
    </button>
  );
}
